<template>
	<InventarisationPageTemplate v-slot="{ selectedIndex, customers }">
		<FormKit
			v-if="customers[selectedIndex] && customer"
			:key="customer.customerId"
			v-model="customer.sections"
			type="form"
			:actions="false"
			:config
		>
			<InventarisationAppendSectionMenu
				:sections
				class="mb-10"
				@append="appendHandler"
			/>

			<div class="pb-10">
				<InventarisationRepeatableSection
					v-for="section in sections"
					:key="section.type"
					:section
					@insert="() => appendHandler(section.type)"
					@remove="(id) => removeHandler(section.type, id)"
				/>
			</div>
		</FormKit>

		<div class="pb-32">
			<KlantgegevensVerplichtingenCard />
		</div>
	</InventarisationPageTemplate>
</template>

<script lang="ts" setup>
import type {
	CustomerLiabilities,
	LiabilitySectionKeys,
	LiabilityType,
} from "@loanlink-nl/model-app";
import { computed, nextTick, watch } from "vue";
import { rootClasses } from "../../../formkit.theme";
import { storeToRefs } from "pinia";
import InventarisationLiabilitiesStudentForm from "@/components/inventarisation/liabilities/InventarisationLiabilitiesStudentForm.vue";
import InventarisationLiabilitiesLeaseForm from "@/components/inventarisation/liabilities/InventarisationLiabilitiesLeaseForm.vue";
import InventarisationLiabilitiesLoanForm from "@/components/inventarisation/liabilities/InventarisationLiabilitiesLoanForm.vue";
import InventarisationLiabilitiesCreditForm from "@/components/inventarisation/liabilities/InventarisationLiabilitiesCreditForm.vue";
import InventarisationLiabilitiesOverdraftForm from "@/components/inventarisation/liabilities/InventarisationLiabilitiesOverdraftForm.vue";
import InventarisationLiabilitiesAlimony from "@/components/inventarisation/liabilities/InventarisationLiabilitiesAlimony.vue";
import KlantgegevensVerplichtingenCard from "@/components/inventarisation/KlantgegevensVerplichtingenCard.vue";
import { useInventarisationStore } from "@/store/useInventarisationStore";

const inventarisationStore = useInventarisationStore();
const { selectedCustomerId: storeSelectedCustomerId } =
	storeToRefs(inventarisationStore);

const {
	customerHasEntry,
	addCustomerEntry,
	getCustomerEntry,
	appendSection,
	removeSection,
	getSection,
	getSectionIndex,
	removeInvalidValues,
} = inventarisationStore.liabilities;

const config = {
	rootClasses,
};

// Ensure customer entry exists when selection changes
watch(
	storeSelectedCustomerId,
	(newSelectedCustomerId) => {
		if (
			newSelectedCustomerId !== undefined &&
			!customerHasEntry(newSelectedCustomerId)
		) {
			addCustomerEntry(newSelectedCustomerId);
		}
		if (newSelectedCustomerId !== undefined) {
			void nextTick(() => {
				removeInvalidValues(newSelectedCustomerId);
			});
		}
	},
	{ immediate: true },
);

// Reactive customer entry - returns the actual reactive object from the store
// Mutations to customer.sections will propagate back to the source
const customer = computed<CustomerLiabilities | undefined>(() => {
	if (storeSelectedCustomerId.value === undefined) {
		return undefined;
	}
	return getCustomerEntry(storeSelectedCustomerId.value).value;
});

const removeHandler = (section: LiabilitySectionKeys, id: string) => {
	if (storeSelectedCustomerId.value) {
		removeSection(storeSelectedCustomerId.value, section, id);
	}
};

const appendHandler = (section: LiabilitySectionKeys) => {
	if (storeSelectedCustomerId.value) {
		appendSection(storeSelectedCustomerId.value, section);
	}
};

const getDefaultHeadingFunction =
	(sectionKey: LiabilityType, label: string, detailsKey?: string) =>
	(id: string) => {
		if (storeSelectedCustomerId.value === undefined) {
			return label;
		}

		const section = getSection(storeSelectedCustomerId.value, sectionKey, id);
		if (
			detailsKey &&
			section?.values &&
			detailsKey in section.values &&
			section.values[detailsKey as keyof (typeof section)["values"]]
		) {
			return `${label} (${(section.values as any)[detailsKey]})`;
		}

		const sectionIndex = getSectionIndex(
			storeSelectedCustomerId.value,
			sectionKey,
			id,
		);
		return `${label} #${sectionIndex + 1}`;
	};

const sections = [
	{
		appendLabel: "Betaalrekening met roodstandmogelijkheid",
		type: "overdraftFacility" as LiabilityType,
		component: InventarisationLiabilitiesOverdraftForm,
		getHeading: getDefaultHeadingFunction(
			"overdraftFacility",
			"Betaalrekening met roodstandmogelijkheid",
			"company",
		),
	},
	{
		appendLabel: "Doorlopend krediet",
		type: "revolvingCredit" as LiabilityType,
		component: InventarisationLiabilitiesCreditForm,
		getHeading: getDefaultHeadingFunction(
			"revolvingCredit",
			"Doorlopend krediet",
			"company",
		),
	},
	{
		appendLabel: "Lening",
		type: "personalLoan" as LiabilityType,
		component: InventarisationLiabilitiesLoanForm,
		getHeading: getDefaultHeadingFunction(
			"personalLoan",
			"Persoonlijke lening",
			"company",
		),
	},
	{
		appendLabel: "Private lease",
		type: "privateLease" as LiabilityType,
		component: InventarisationLiabilitiesLeaseForm,
		getHeading: getDefaultHeadingFunction(
			"privateLease",
			"Private lease",
			"company",
		),
	},
	{
		appendLabel: "Studieschuld (SF15)",
		type: "studentDebtSF15" as LiabilityType,
		component: InventarisationLiabilitiesStudentForm,
		getHeading: getDefaultHeadingFunction(
			"studentDebtSF15",
			"Studieschuld (SF15)",
		),
	},
	{
		appendLabel: "Studieschuld (SF35)",
		type: "studentDebtSF35" as LiabilityType,
		component: InventarisationLiabilitiesStudentForm,
		getHeading: getDefaultHeadingFunction(
			"studentDebtSF35",
			"Studieschuld (SF35)",
		),
	},
	{
		appendLabel: "Partneralimentatie",
		type: "spousalAlimony" as LiabilityType,
		component: InventarisationLiabilitiesAlimony,
		getHeading: getDefaultHeadingFunction(
			"spousalAlimony",
			"Alimentatie ex-partner",
		),
	},
];
</script>
